import { useEffect, useState } from 'react';
import { Button, Card, Flex, InputNumber, Segmented, Slider, Space, Typography, message } from 'antd';
import { useCalibration } from '../../app/calibration-context';
import { CalibrationNotice } from '../../components/PhysicalSize';
import {
  DEFAULT_PX_PER_CM,
  ID1_CARD_CM,
  MAX_PX_PER_CM,
  MIN_PX_PER_CM,
  cmToPx,
  isValidPxPerCm,
  pxPerCmFromMeasurement,
} from '../../lib/calibration';

const RULER_CM = 10;
type Reference = 'ruler' | 'card';

/** 1cm 눈금이 있는 기준 막대 */
function RulerBar({ pxPerCm }: { pxPerCm: number }) {
  const width = cmToPx(RULER_CM, pxPerCm);
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <div data-testid="ruler-bar" style={{ position: 'relative', width, height: 36, borderBottom: '2px solid #1677ff', boxSizing: 'content-box' }}>
        {Array.from({ length: RULER_CM + 1 }, (_, i) => (
          <div key={i} style={{ position: 'absolute', left: cmToPx(i, pxPerCm) - (i === RULER_CM ? 2 : 0), bottom: 0, width: 2, height: i % 5 === 0 ? 28 : 14, background: '#1677ff' }}>
            {i % 5 === 0 && <span style={{ position: 'absolute', top: -16, left: -4, fontSize: 11, color: '#1677ff' }}>{i}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function CardOutline({ pxPerCm }: { pxPerCm: number }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        data-testid="card-outline"
        style={{ width: cmToPx(ID1_CARD_CM.width, pxPerCm), height: cmToPx(ID1_CARD_CM.height, pxPerCm), border: '2px solid #1677ff', borderRadius: cmToPx(0.3, pxPerCm), boxSizing: 'border-box' }}
      />
    </div>
  );
}

export function CalibrationPanel() {
  const { status, save, reset } = useCalibration();
  const [draft, setDraft] = useState(status.pxPerCm);
  const [reference, setReference] = useState<Reference>('ruler');
  const [measured, setMeasured] = useState<number | null>(null);
  const [msg, holder] = message.useMessage();

  // 저장·초기화·화면 변경으로 적용값이 바뀌면 조정값도 맞춘다.
  useEffect(() => setDraft(status.pxPerCm), [status.pxPerCm]);

  const applyMeasurement = () => {
    if (measured == null) return;
    const v = pxPerCmFromMeasurement(cmToPx(RULER_CM, draft), measured);
    if (v == null) {
      msg.error('측정값이 너무 크거나 작습니다. 막대 전체 길이를 cm로 입력해 주세요.');
      return;
    }
    setDraft(v);
    setMeasured(null);
  };

  const dirty = Math.abs(draft - status.pxPerCm) > 1e-9;

  return (
    <Card title="화면 보정" size="small">
      {holder}
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <CalibrationNotice showLink={false} />
        <Segmented<Reference>
          value={reference}
          onChange={setReference}
          options={[{ value: 'ruler', label: '자로 재기' }, { value: 'card', label: '신용카드에 맞추기' }]}
        />
        {reference === 'ruler' ? (
          <>
            <Typography.Paragraph style={{ margin: 0 }}>
              자를 화면에 대고 아래 파란 막대의 0부터 {RULER_CM}까지 길이를 재세요. 잰 길이를 입력하거나, 슬라이더로 막대가 정확히 {RULER_CM}cm가 되도록 맞춥니다.
            </Typography.Paragraph>
            <RulerBar pxPerCm={draft} />
            <Flex gap={8} align="center" wrap>
              <InputNumber<number>
                aria-label="측정한 길이(cm)"
                placeholder="측정한 길이"
                suffix="cm"
                min={0.1}
                max={100}
                step={0.1}
                value={measured}
                onChange={setMeasured}
                onPressEnter={applyMeasurement}
                style={{ width: 180 }}
              />
              <Button onClick={applyMeasurement} disabled={measured == null}>측정값 반영</Button>
            </Flex>
          </>
        ) : (
          <>
            <Typography.Paragraph style={{ margin: 0 }}>
              신용카드·교통카드처럼 표준 크기({ID1_CARD_CM.width}×{ID1_CARD_CM.height}cm) 카드를 화면에 대고, 테두리가 카드와 같아지도록 슬라이더로 맞춥니다.
            </Typography.Paragraph>
            <CardOutline pxPerCm={draft} />
          </>
        )}
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>미세 조정</Typography.Text>
          <Slider aria-label="보정 미세 조정" min={MIN_PX_PER_CM} max={MAX_PX_PER_CM} step={0.01} value={draft} onChange={(v) => isValidPxPerCm(v) && setDraft(v)} tooltip={{ formatter: (v) => `1cm = ${v?.toFixed(2)}px` }} />
        </div>
        <Flex gap={8} align="center" wrap>
          <Button type="primary" disabled={!dirty && status.state === 'calibrated'} onClick={() => { save(draft); msg.success('이 화면의 보정값을 저장했습니다.'); }}>
            보정값 저장
          </Button>
          <Button onClick={() => setDraft(DEFAULT_PX_PER_CM)}>규격값으로</Button>
          {status.state === 'calibrated' && <Button danger onClick={reset}>저장값 삭제</Button>}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            1cm = {draft.toFixed(2)}px (규격 {DEFAULT_PX_PER_CM.toFixed(2)}px){dirty ? ' · 저장하지 않음' : ''}
          </Typography.Text>
        </Flex>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          보정값은 이 브라우저에 화면별로 저장됩니다. 다른 모니터나 기기에서는 따로 보정해야 합니다.
        </Typography.Text>
      </Space>
    </Card>
  );
}
